import { FILTER_CONFIG_NAMES } from './DataConstants';
import { isBuyerOrInternalTech, isInternalTech } from './AuthUtils';

// Shared sort options for the buyer and supplier asset index pages.
// `order: 'ascend'` maps to `asc` in restCrudThunksGenerator; anything else maps to `desc`.
//
// NOTE: These sorts are only honored on the OpenSearch read path (searchWithPostMethod),
// which is gated behind the `assets-openwsearch` flag — so only pass these to ReduxDataTable
// when that flag is enabled. The backend (AssetOpenSearchHelper.getSortQueries) only supports
// `daysInUse` (asc/desc) and `name` ascending; name descending is intentionally omitted because
// the backend ignores order for name and always returns A–Z.
export const ASSET_SORTERS = [
	{ label: 'Name (A–Z)', selectVal: 'name_asc', value: { sort_by: 'name', order: 'ascend' } },
	{
		// Oldest effective age = most time in use = highest daysInUse.
		label: 'Age (oldest)',
		selectVal: 'daysInUse_desc',
		value: { sort_by: 'daysInUse', order: 'desc' },
	},
	{
		// Newest effective age = least time in use = lowest daysInUse.
		label: 'Age (newest)',
		selectVal: 'daysInUse_asc',
		value: { sort_by: 'daysInUse', order: 'ascend' },
	},
];

// Strip handleChange so ReduxDataTable uses its own internal handler
// which triggers both state update and data refetch via updateFiltersEntities.
const stripHandleChange = (config) => {
	if (!config) return config;
	const { handleChange, ...rest } = config;
	return rest;
};

export const getAssetFilterConfig = ({
	filters,
	allowArea = false,
	currentUser = null,
	removeAssetNumberToQRCodeAssociation = false,
	replacementIndexEnabled = false,
}: {
	filters: any;
	allowArea?: boolean;
	currentUser?: any;
	removeAssetNumberToQRCodeAssociation?: boolean;
	replacementIndexEnabled?: boolean;
}) => {
	return [
		filters[FILTER_CONFIG_NAMES.SERIAL_NUMBER],
		filters[FILTER_CONFIG_NAMES.LOCATION],
		...(allowArea ? [filters[FILTER_CONFIG_NAMES.AREA]] : []),
		...(isBuyerOrInternalTech(currentUser) ? [filters[FILTER_CONFIG_NAMES.ASSET_TYPE]] : []),
		filters[FILTER_CONFIG_NAMES.DOWNTIME_STATUS],
		filters[FILTER_CONFIG_NAMES.ASSET_HAS_WARRANTY],
		filters[FILTER_CONFIG_NAMES.TOP_LEVEL_ONLY],
		...(removeAssetNumberToQRCodeAssociation ? [filters[FILTER_CONFIG_NAMES.ASSET_TAGGED]] : []),
		...(replacementIndexEnabled ? [filters[FILTER_CONFIG_NAMES.REPLACEMENT_INDEX]] : []),
		filters[FILTER_CONFIG_NAMES.IS_ACTIVE] && {
			...filters[FILTER_CONFIG_NAMES.IS_ACTIVE],
			showOutsideDropdown: true,
			info: undefined,
		},
		filters[FILTER_CONFIG_NAMES.INSTALL_AT],
	]
		.filter(Boolean)
		.map(stripHandleChange);
};

export const getSupplierAssetFilterConfig = ({
	filters,
	allowArea = false,
	currentUser = null,
	showDepreciationClass = false,
	removeAssetNumberToQRCodeAssociation = false,
}: {
	filters: any;
	allowArea?: boolean;
	currentUser?: any;
	showDepreciationClass?: boolean;
	removeAssetNumberToQRCodeAssociation?: boolean;
}) => {
	return [
		filters[FILTER_CONFIG_NAMES.SERIAL_NUMBER],
		filters[FILTER_CONFIG_NAMES.LOCATION],
		...(allowArea ? [filters[FILTER_CONFIG_NAMES.AREA]] : []),
		filters[FILTER_CONFIG_NAMES.ASSET_TYPE],
		filters[FILTER_CONFIG_NAMES.MANUFACTURER],
		...(isInternalTech(currentUser) ? [filters[FILTER_CONFIG_NAMES.VENDOR]] : []),
		...(showDepreciationClass ? [filters[FILTER_CONFIG_NAMES.DEPRECIATION_CLASS]] : []),
		filters[FILTER_CONFIG_NAMES.ADDITIONAL_FIELD],
		filters[FILTER_CONFIG_NAMES.INSTALL_AT],
		filters[FILTER_CONFIG_NAMES.ASSET_HAS_WARRANTY],
		...(removeAssetNumberToQRCodeAssociation ? [filters[FILTER_CONFIG_NAMES.ASSET_TAGGED]] : []),
		filters[FILTER_CONFIG_NAMES.DOWNTIME_STATUS],
		filters[FILTER_CONFIG_NAMES.IS_ACTIVE] && {
			...filters[FILTER_CONFIG_NAMES.IS_ACTIVE],
			showOutsideDropdown: true,
			info: undefined,
		},
	]
		.filter(Boolean)
		.map(stripHandleChange);
};
